import React, { ReactElement, useRef, useState } from 'react'
import { ActionMeta } from 'react-select'
import Creatable from 'react-select/creatable'
import i18n from 'live/services/i18n'
import Tooltip from '../GeneralTextInput/Tooltip'
import type { OptionType } from '../../types/utilities'
import { setOptions } from './helper'
import { Title, Text, Header, IconHeader, IconBottom, DivIcon, Asterisk, BottomText, Placeholder } from './styles'
import styled from 'styled-components'

const CLASS_PREFIX = `generalInformation--recipientsGroups__recipientsSelect`
interface EmailInputRecipientProps {
    onChange: (arg: string[], numberOfEmails?: number) => void
    values?: string[]
    required: boolean
    variant: 'primary' | 'secondary'
    displayMsg?: boolean
    showTotalEmail?: boolean
    editMode: boolean
    title: string
    tooltip?: string
    bottomText?: string
    className?:string
}

const EmailInputRecipient = ({
    onChange,
    values = [],
    displayMsg = false,
    showTotalEmail = false,
    editMode,
    title,
    tooltip,
    required,
    variant,
    bottomText,
    className
}: EmailInputRecipientProps): ReactElement => {
    const [msg, setMsg] = useState<string>()
    const emailInputRef = useRef(null)

    const handleCreatable = (emails: OptionType[], event: ActionMeta<OptionType>): void => {
        const { eValues, msg, changes } = setOptions(values, emails, event)
        onChange(eValues, eValues.length)
        if (displayMsg && msg !== 'success') setMsg(msg)
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        const currentInputValue = emailInputRef.current.state.inputValue
        if (event.key === ' ' || event.key === ',') {
            const { eValues, msg } = setOptions(
                values,
                [{ value: currentInputValue, label: currentInputValue }] as OptionType[],
                {
                    action: 'create-option'
                }
            )
            onChange(eValues, eValues.length)
            if (msg === 'success') emailInputRef.current.state.inputValue = ''
        }
    }

    return (
        <StyledEmailInputRecipient className={`${CLASS_PREFIX}_${variant} ${className}`} tabIndex={0}>
            {displayMsg ? (
                <div>
                    {showTotalEmail ? <label>total:{values.length}</label> : null}
                    <label>{msg || ''}</label>
                </div>
            ) : null}
            <Header className={className+'__header'}>
                {title ? <Title>{title}</Title> : null}
                {tooltip ? (
                    <DivIcon>
                        <Tooltip className={'emailInputRecipient_tooltip'} text={tooltip} maxWidth={'190px'} />
                        <IconHeader className={'far fa-info-circle'} />
                    </DivIcon>
                ) : null}
                {required ? <Asterisk>*</Asterisk> : null}
            </Header>
            <StyledCreatable
                ref={emailInputRef}
                classNamePrefix={CLASS_PREFIX}
                value={values.map(email => ({ value: email, label: email } as OptionType))}
                placeholder={i18n('Adicione emails...')}
                isMulti={true}
                onChange={handleCreatable}
                isDisabled={!editMode}
                isClearable={false}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null, Placeholder }}
                openMenuOnClick={false}
                onKeyDown={onKeyDown}
            />
            {bottomText ? (
                <BottomText className={className+'__bottomText'}>
                    <IconBottom className={'far fa-info-circle'} />
                    <Text>{bottomText}</Text>
                </BottomText>
            ) : null}
        </StyledEmailInputRecipient>
    )
}

export default EmailInputRecipient
export type { EmailInputRecipientProps }

export const StyledEmailInputRecipient = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100%;
    width: 100%;
    border: 1px solid ${({ theme }) => theme['gray-200']};
    background-color: 'transparent';
    transition: 0.3s;
    &:focus-within {
        background-color: transparent;
    }
    &:focus {
        background-color: transparent;
    }

    .${CLASS_PREFIX}__option {
        color: ${props => props.theme['gray-800']};
        padding: 10;
    }
    .${CLASS_PREFIX}__control {
        border: 1px solid ${({ theme }) => theme['gray-500']};
        background: ${({ theme }) => theme['gray-0']};
        max-height: 90px;
        margin-top: 5px;
        overflow: auto;
    }
    .${CLASS_PREFIX}__multi-value {
        border: 1px solid ${({ theme }) => theme['gray-400']};
    }
    .${CLASS_PREFIX}__multi-value__label {
        border-right: 1px solid ${({ theme }) => theme['gray-400']};
    }
    .${CLASS_PREFIX}__indicatorSeparator {
        display: none;
    }
    .${CLASS_PREFIX}__indicatorsContainer {
        display: none;
    }

    &.${CLASS_PREFIX}_secondary {
        padding: 7px 15px;
        &:focus-within,
        &:focus {
            background-color: ${props => props.theme['blue-200']};
        }

        .${CLASS_PREFIX}__input {
            display: 'flex';
            justify-content: 'center';
            align-items: 'center';
            align-content: 'center';
            margin: '0px';
            padding-bottom: '-5px !important';
            height: '16px';
        }
        .${CLASS_PREFIX}__control {
            background: transparent;

            &,
            &--is-focused {
                border: none;
                box-shadow: none;
            }
            max-height: 90px;
            overflow: auto;
        }
    }
`

const StyledCreatable = styled(Creatable)``
